// Screen languages, chosen with the language button at the top right of every screen (the only place to change it).
// Two kinds of wording files:
//  - keyed files (en.ts, ar.ts, zh.ts): the QC inspector screens' wording by key; English is complete and is the
//    fallback for any key a language doesn't have, so a missing key shows English, never a blank or a raw key;
//  - phrase files (web/public/i18n/<code>.json, see tools/screen-phrases.mjs): every other screen's English wording →
//    its translation, swapped in on screen by web/src/screenlang.ts.
// Artwork, labels, CAD, documents (PO, tech pack, PDFs) and anything people type are never translated.
// To add a language: one line in LANGS and LANG_ENGLISH_NAME, and its phrase file (a keyed file is optional).
import { EN } from "./en.js";
import { AR } from "./ar.js";
import { ZH } from "./zh.js";

export interface LangInfo { code: string; name: string; dir: "ltr" | "rtl"; strings: Record<string, string> }
export const LANGS: LangInfo[] = [
  { code: "en", name: "English", dir: "ltr", strings: EN },
  { code: "zh", name: "中文 (简体)", dir: "ltr", strings: ZH },
  { code: "ar", name: "العربية", dir: "rtl", strings: AR },
  { code: "es", name: "Español", dir: "ltr", strings: {} },
  { code: "fr", name: "Français", dir: "ltr", strings: {} },
  { code: "de", name: "Deutsch", dir: "ltr", strings: {} },
  { code: "pt", name: "Português", dir: "ltr", strings: {} },
  { code: "ru", name: "Русский", dir: "ltr", strings: {} },
  { code: "hi", name: "हिन्दी", dir: "ltr", strings: {} },
  { code: "bn", name: "বাংলা", dir: "ltr", strings: {} },
  { code: "ur", name: "اردو", dir: "rtl", strings: {} },
  { code: "tr", name: "Türkçe", dir: "ltr", strings: {} },
  { code: "vi", name: "Tiếng Việt", dir: "ltr", strings: {} },
  { code: "id", name: "Bahasa Indonesia", dir: "ltr", strings: {} },
  { code: "ja", name: "日本語", dir: "ltr", strings: {} },
  { code: "ko", name: "한국어", dir: "ltr", strings: {} },
  { code: "it", name: "Italiano", dir: "ltr", strings: {} },
];
/** Languages with their own keyed wording for the QC inspector screens (the others use the phrase files there too). */
export const KEYED_LANGS = ["ar", "zh"];
/** The short code shown on the language button ("EN", "中文", "AR"…). */
export const langButtonCode = (code: string) => (code === "zh" ? "中文" : code.toUpperCase());
export const LANG_CODES = LANGS.map((l) => l.code);
export const DEFAULT_LANG = "en";
export const normalizeLang = (v: unknown): string => (LANG_CODES.includes(String(v)) ? String(v) : DEFAULT_LANG);
export const langInfo = (code: string): LangInfo => LANGS.find((l) => l.code === code) || LANGS[0];
/** The English name of a language (for office screens and the Activity Log). */
export const LANG_ENGLISH_NAME: Record<string, string> = {
  en: "English", zh: "Chinese (Simplified)", ar: "Arabic", es: "Spanish", fr: "French", de: "German", pt: "Portuguese", ru: "Russian",
  hi: "Hindi", bn: "Bengali", ur: "Urdu", tr: "Turkish", vi: "Vietnamese", id: "Indonesian", ja: "Japanese", ko: "Korean", it: "Italian",
};

/** A key's wording in a language, with {name} placeholders filled in. Missing in that language → English. */
export function translate(lang: string, key: string, vars?: Record<string, string | number>): string {
  const own = langInfo(lang).strings[key];
  let s = own !== undefined && own !== "" ? own : EN[key];
  if (s === undefined) s = key.startsWith("@") ? key.slice(1) : "";   // "@text": a literal (never shown as a raw key)
  if (vars) s = s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? String(vars[k]) : m));
  return s;
}
